import { useId, useState } from 'react';

import { Button } from '../Button';
import { Icon } from '../Icon';

/** What an approval commits, captured at the moment it is given. */
export interface ApprovalRecord {
  /** The action approved, verbatim from what the approver was shown. */
  readonly action: string;
  /** Who approved it. */
  readonly approver: string;
  /** In what capacity. */
  readonly approverRole: string;
  /** When, ISO-8601. */
  readonly approvedAt: string;
}

/**
 * What a person attests by approving an assistant draft in one press.
 *
 * The server records these exact words with the approval, so a screen that asks for the single
 * press shows them beside the button rather than a paraphrase.
 */
export const DRAFT_ATTESTATION =
  'I have read this exact draft and approve it to be applied as written.';

export interface ApprovalGateProps {
  readonly approveLabel?: string;
  /**
   * The action in plain words, as it will be recorded.
   *
   * This exact string goes into the audit trail, so the approver and the record
   * cannot describe two different things.
   */
  readonly action: string;
  /** What actually changes if this is approved. */
  readonly consequence: string;
  /** The signed-in person who would be approving. */
  readonly approver: string;
  /** Their role, which is what the permission model checks. */
  readonly approverRole: string;
  /** Called with the record when approval is given. */
  readonly onApprove: (record: ApprovalRecord) => void;
  /** Called when the approver declines. */
  readonly onCancel: () => void;
  /**
   * What the decline control says. Name the consequence when declining is itself
   * a recorded decision (a rejection), rather than a harmless "Cancel".
   */
  readonly cancelLabel?: string;
  /** Disables the decline control, e.g. while a decision is being saved. */
  readonly cancelDisabled?: boolean;
  /** Disables approval with a reason, e.g. when the role is not permitted. */
  readonly blockedReason?: string;
  /**
   * Whether approving also needs a ticked statement of responsibility. It does by default.
   *
   * Leave it on wherever the approval commits something the approver may not have read closely.
   * Turn it off only where the exact action is the whole of what is on screen and the button is
   * named for it: the responsibility is then stated beside the button, and the click is the
   * deliberate act.
   */
  readonly requireAttestation?: boolean;
  /**
   * What is stated beside the button when no tick is asked for. Defaults to a sentence taking
   * responsibility for the action as the approver's role.
   *
   * Give it when the click records a specific attestation, so that what the person was shown is
   * what is recorded.
   */
  readonly statement?: string;
}

/**
 * The checkpoint in front of anything the assistant would otherwise execute.
 *
 * Level 3 actions in the operating model — the ones that change records, send
 * things to customers, or move money — are allowed only after a named human
 * approves them *in the app*. This is that control, and it is deliberately
 * slower than a confirm dialog.
 *
 * Two frictions are on purpose. The approver must tick an attestation, which
 * cannot be satisfied by a stray Enter keypress on a focused default button.
 * And the approval is captured as a record naming the person, their role, the
 * action text they were shown and the moment — not as a boolean — because six
 * months later the question is never "was it approved" but "who approved this,
 * as what, and what exactly did they see".
 *
 * Nothing here trusts the client: this produces the claim that the server then
 * re-checks against the approver's permissions.
 *
 * @param props see {@link ApprovalGateProps}
 * @returns the approval control
 */
export function ApprovalGate({
  approveLabel = 'Approve and run',
  action,
  consequence,
  approver,
  approverRole,
  onApprove,
  onCancel,
  cancelLabel = 'Cancel',
  cancelDisabled = false,
  blockedReason,
  requireAttestation = true,
  statement,
}: ApprovalGateProps): React.JSX.Element {
  const [attested, setAttested] = useState(false);
  const checkboxId = `${useId()}-attest`;
  const blocked = blockedReason !== undefined;

  return (
    <div className="fo-approval" role="group" aria-label="Approval required">
      <p className="fo-approval__heading">
        <Icon name="lock" />
        Approval required before this runs
      </p>

      <dl className="fo-approval__facts">
        <div>
          <dt>Action</dt>
          <dd data-testid="approval-action">{action}</dd>
        </div>
        <div>
          <dt>Effect</dt>
          <dd>{consequence}</dd>
        </div>
        <div>
          <dt>Approving as</dt>
          <dd>
            {approver} · {approverRole}
          </dd>
        </div>
      </dl>

      {blocked ? (
        <p className="fo-approval__blocked" role="alert">
          {blockedReason}
        </p>
      ) : requireAttestation ? (
        <div className="fo-approval__attest">
          <input
            id={checkboxId}
            type="checkbox"
            checked={attested}
            onChange={(event) => {
              setAttested(event.target.checked);
            }}
          />
          <label htmlFor={checkboxId}>
            I have read this action and I take responsibility for it as {approverRole}.
          </label>
        </div>
      ) : (
        <p className="fo-approval__statement">
          {statement ?? `Approving takes responsibility for this action as ${approverRole}.`}
        </p>
      )}

      <div className="fo-approval__actions">
        <Button
          variant="primary"
          icon="check"
          disabled={blocked || (requireAttestation && !attested)}
          onClick={() => {
            onApprove({
              action,
              approver,
              approverRole,
              approvedAt: new Date().toISOString(),
            });
          }}
        >
          {approveLabel}
        </Button>
        <Button variant="ghost" disabled={cancelDisabled} onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
}
